//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import * as instagramHashtagsApi from '@apis/instagram-hashtag/instagramHashtagApi';
import {
  IInstagramDailyHistoricalMetrics,
  InstagramHashtagTopContentSortKey,
} from '@apis/instagram-hashtag/instagramHashtagApiModel';
import {
  HASHTAG_INSIGHTS_REQUEST_LIMIT,
  IInstagramHashtagInsightsState,
  IInstagramHashtagOverallMetrics,
} from '@instagram-account/redux/instagram-hashtag/insights/instagramHashtagInsightsModel';
import { dailyMetricsToOverallMetrics } from '@instagram-account/redux/instagram-hashtag/insights/instagramHashtagInsightsUtils';
import { IInstagramPost } from '@models/IInstagramMediaItem';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';

const initialState: IInstagramHashtagInsightsState = {
  content: {
    offset: 0,
    items: [],
    sort: InstagramHashtagTopContentSortKey.RECENT,
  },
  errors: {
    fetchInstagramHashtagHistoricalMetricsError: undefined,
    fetchInstagramHashtagTopContentError: undefined,
    fetchMoreInstagramHashtagTopContentError: undefined,
  },
  flags: {
    isEndOfInstagramHashtagTopContentReached: false,
    isFetchingInstagramHashtagHistoricalMetrics: false,
    isFetchingInstagramHashtagTopContent: false,
    isFetchingMoreInstagramHashtagTopContent: false,
  },
  metrics: {
    dailyHistoricalMetrics: [],
    overallMetrics: {
      totalLikes: 0,
      totalComments: 0,
      totalInteractions: 0,
      totalPosts: 0,
    },
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramHashtagInsights,
  initialState,
  reducers: {
    setInstagramHashtagTopContentSort(
      state,
      action: PayloadAction<InstagramHashtagTopContentSortKey>,
    ) {
      state.content.sort = action.payload;
    },
    requestInstagramHistoricalMetrics(state) {
      state.flags.isFetchingInstagramHashtagHistoricalMetrics = true;
    },
    receiveInstagramHistoricalMetricsSucceed(
      state,
      action: PayloadAction<{
        dailyMetrics: IInstagramDailyHistoricalMetrics[];
        overallMetrics: IInstagramHashtagOverallMetrics;
      }>,
    ) {
      state.flags.isFetchingInstagramHashtagHistoricalMetrics = false;
      state.metrics.dailyHistoricalMetrics = action.payload.dailyMetrics;
      state.metrics.overallMetrics = {
        ...state.metrics.overallMetrics,
        ...action.payload.overallMetrics,
      };
    },
    receiveInstagramHistoricalMetricsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingInstagramHashtagHistoricalMetrics = false;
      state.errors.fetchInstagramHashtagHistoricalMetricsError = action.payload;
    },
    requestInstagramTopContent(state) {
      state.flags.isFetchingInstagramHashtagTopContent = true;
    },
    receiveInstagramTopContentSucceed(
      state,
      action: PayloadAction<IInstagramPost[]>,
    ) {
      state.flags.isEndOfInstagramHashtagTopContentReached =
        action.payload.length < HASHTAG_INSIGHTS_REQUEST_LIMIT;
      state.flags.isFetchingInstagramHashtagTopContent = false;
      state.content.items = [...action.payload];
      state.content.offset = action.payload.length;
    },
    receiveInstagramHashtagTopContentFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingInstagramHashtagTopContent = false;
      state.errors.fetchInstagramHashtagTopContentError = action.payload;
    },
    requestMoreInstagramTopContent(state) {
      state.flags.isFetchingMoreInstagramHashtagTopContent = true;
    },
    receiveMoreInstagramTopContentSucceed(
      state,
      action: PayloadAction<IInstagramPost[]>,
    ) {
      state.flags.isEndOfInstagramHashtagTopContentReached =
        action.payload.length < HASHTAG_INSIGHTS_REQUEST_LIMIT;
      state.flags.isFetchingMoreInstagramHashtagTopContent = false;
      state.content.items = [...state.content.items, ...action.payload];
      state.content.offset = state.content.offset + action.payload.length;
    },
    receiveMoreInstagramHashtagTopContentFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingMoreInstagramHashtagTopContent = false;
      state.errors.fetchMoreInstagramHashtagTopContentError = action.payload;
    },
    resetContentState(state) {
      state.content.offset = 0;
      state.content.items = [];
      state.flags.isEndOfInstagramHashtagTopContentReached = false;
    },
    resetHashtagInsightsState(state) {
      return initialState;
    },
  },
});

export const setupInstagramInsightsPage = createThunk(
  () => async (dispatch, getState) => {
    dispatch(actions.resetContentState());
    dispatch(fetchInstagramHistoricalMetrics());
    dispatch(fetchInstagramTopContent());
  },
);

export const fetchInstagramHistoricalMetrics = createThunk(
  () => async (dispatch, getState) => {
    const { dateRange, hashtag } = getState().instagram.hashtags.page;
    const {
      flags: { isFetchingInstagramHashtagHistoricalMetrics },
    } = getState().instagram.hashtags.insights;

    if (isFetchingInstagramHashtagHistoricalMetrics) {
      return;
    }

    dispatch(actions.requestInstagramHistoricalMetrics());

    try {
      const dailyMetrics = await instagramHashtagsApi.getDailySourceMetrics({
        sourceId: hashtag,
        startTime: dateRange.startDate,
        endTime: dateRange.endDate,
      });

      const overallMetrics = dailyMetricsToOverallMetrics(dailyMetrics);

      dispatch(
        actions.receiveInstagramHistoricalMetricsSucceed({
          dailyMetrics,
          overallMetrics,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.receiveInstagramHistoricalMetricsFailed(
          'An error occurred while fetching historical metrics. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const fetchInstagramTopContent = createThunk(
  () => async (dispatch, getState) => {
    const { dateRange, hashtag } = getState().instagram.hashtags.page;
    const {
      content: { offset, sort },
      flags: { isFetchingInstagramHashtagTopContent },
    } = getState().instagram.hashtags.insights;

    if (isFetchingInstagramHashtagTopContent) {
      return;
    }

    dispatch(actions.requestInstagramTopContent());

    try {
      const instagramPosts = await instagramHashtagsApi.getTopContent({
        endTime: dateRange.endDate,
        limit: HASHTAG_INSIGHTS_REQUEST_LIMIT,
        offset,
        sort,
        sourceId: hashtag,
        startTime: dateRange.startDate,
      });

      dispatch(actions.receiveInstagramTopContentSucceed(instagramPosts));
    } catch (_e) {
      dispatch(
        actions.receiveInstagramHashtagTopContentFailed(
          'An error occurred while fetching top content. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const fetchMoreInstagramTopContent = createThunk(
  () => async (dispatch, getState) => {
    const { dateRange, hashtag } = getState().instagram.hashtags.page;
    const {
      content: { offset, sort },
      flags: {
        isEndOfInstagramHashtagTopContentReached,
        isFetchingMoreInstagramHashtagTopContent,
      },
    } = getState().instagram.hashtags.insights;

    if (
      isFetchingMoreInstagramHashtagTopContent ||
      isEndOfInstagramHashtagTopContentReached
    ) {
      return;
    }

    dispatch(actions.requestMoreInstagramTopContent());

    try {
      const instagramPosts = await instagramHashtagsApi.getTopContent({
        endTime: dateRange.endDate,
        limit: HASHTAG_INSIGHTS_REQUEST_LIMIT,
        offset,
        sort,
        sourceId: hashtag,
        startTime: dateRange.startDate,
      });

      dispatch(actions.receiveMoreInstagramTopContentSucceed(instagramPosts));
    } catch (_e) {
      dispatch(
        actions.receiveMoreInstagramHashtagTopContentFailed(
          'An error occurred while fetching top content. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const generateInstagramHistoricalMetricsCsv = createThunk(
  () => async (dispatch, getState) => {
    const { dateRange, hashtag } = getState().instagram.hashtags.page;

    return instagramHashtagsApi.getDailyMetricsCsv({
      endTime: dateRange.endDate,
      sourceId: hashtag,
      startTime: dateRange.startDate,
    });
  },
);

export const generateInstagramTopContentCsv = createThunk(
  () => async (dispatch, getState) => {
    const { dateRange, hashtag } = getState().instagram.hashtags.page;
    const {
      content: { sort },
    } = getState().instagram.hashtags.insights;

    return instagramHashtagsApi.getTopContentCsv({
      endTime: dateRange.endDate,
      sort,
      sourceId: hashtag,
      startTime: dateRange.startDate,
    });
  },
);

export const setSortAndRefreshContent = createThunk(
  (sort: InstagramHashtagTopContentSortKey) => async (dispatch, getState) => {
    dispatch(actions.resetContentState());
    dispatch(actions.setInstagramHashtagTopContentSort(sort));
    dispatch(fetchInstagramTopContent());
  },
);

export const { resetHashtagInsightsState } = actions;

export default reducer;
